import { createContext, useContext, type ReactNode } from 'react'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { LEGACY_AUTHZ_CONTEXT, useAuthzContextQuery, type AuthzContextState } from './useAuthzContextQuery'

const AuthzContext = createContext<AuthzContextState | undefined>(undefined)

const ResolvedAuthzContext = ({ children }: { children: ReactNode }) => {
  const authzContext = useAuthzContextQuery()

  return <AuthzContext.Provider value={authzContext}>{children}</AuthzContext.Provider>
}

/**
 * Resolves what the current user has access to once, for every consumer. Mounted inside the
 * authenticated providers, so an unauthenticated route never renders it and `useAuthzContext` throws
 * there rather than answering permissively.
 */
export const AuthzContextProvider = ({ children }: { children: ReactNode }) => {
  const { FGA_POC } = useFeatureFlags()

  if (FGA_POC !== true) return <AuthzContext.Provider value={LEGACY_AUTHZ_CONTEXT}>{children}</AuthzContext.Provider>
  return <ResolvedAuthzContext>{children}</ResolvedAuthzContext>
}

export const useAuthzContext = (): AuthzContextState => {
  const context = useContext(AuthzContext)
  if (context == null) {
    throw new Error('useAuthzContext must be used within an AuthzContextProvider')
  }
  return context
}
